import { PropsWithChildren } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import styled, { css } from "styled-components";
import { SelectOption } from "@shape/reef-shared";
import Checkbox from "../../ui/Checkbox";
import { BaseField, BaseFieldProps } from "./BaseField";

const ReadOnlyContainer = styled.div`
  div {
    margin-bottom: 16px;
  }
`;

const Container = styled.div<{ isHorizontal: boolean }>`
  display: flex;

  ${({ isHorizontal }) =>
    isHorizontal
      ? css`
          align-items: center;

          .checkbox-container {
            margin-right: 16px;
          }
        `
      : css`
          flex-direction: column;

          .checkbox-container {
            margin-bottom: 8px;
          }
        `}
`;

export interface CheckboxOptionProps extends SelectOption {
  title?: string;
}

interface CheckboxGroupFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> extends BaseFieldProps<TFieldValues, TName> {
  options: CheckboxOptionProps[];
  layout?: "horizontal" | "vertical";
  defaultValue?: (number | string)[];
  onChange?: (value: (number | string)[]) => void;
}

export function CheckboxGroupField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  name,
  label,
  options,
  defaultValue,
  rules,
  form,
  layout,
  readOnly,
  onChange,
}: PropsWithChildren<CheckboxGroupFieldProps<TFieldValues, TName>>) {
  return (
    <BaseField
      form={form}
      name={name}
      label={label}
      defaultValue={
        defaultValue?.length
          ? defaultValue.filter((value: string | number) =>
              options.find((option) => option.id === value)
            )
          : []
      }
      rules={{
        ...rules,
        validate: rules?.required
          ? (value) => {
              if (!value.length) {
                return typeof rules.required === "string"
                  ? rules.required
                  : false;
              }
              return undefined;
            }
          : undefined,
      }}
      fieldClassName="checkbox-group-field"
      render={({ field }) =>
        readOnly ? (
          <ReadOnlyContainer>
            {!!field.value.length &&
              options
                .filter((option) =>
                  field.value.includes(option.id ?? option.label)
                )
                .map((option) => (
                  <>
                    {option.title && <h5>{option.title}</h5>}
                    <div key={option.label}>{option.label}</div>
                  </>
                ))}
          </ReadOnlyContainer>
        ) : (
          <Container isHorizontal={layout === "horizontal"}>
            {options.map((option) => {
              const optionValue = option.id ?? option.label;
              const isSelected = field.value?.includes(optionValue);
              return (
                <div
                  key={`${name}-${optionValue}`}
                  style={{ marginBottom: option.title ? "14px" : "0" }}
                >
                  {option.title && <h5>{option.title}</h5>}
                  <Checkbox
                    name={`${name}-${optionValue}`}
                    label={option.label}
                    checked={isSelected}
                    onChange={() => {
                      const newValue = field.value?.filter(
                        (item: string | number) => item !== optionValue
                      );
                      if (!isSelected) {
                        newValue.push(optionValue);
                      }
                      field.onChange(newValue);
                      onChange?.(newValue);
                    }}
                  />
                </div>
              );
            })}
          </Container>
        )
      }
    />
  );
}

CheckboxGroupField.defaultProps = {
  defaultValue: [],
  layout: "vertical",
};
